'use strict';

/**
 * Friendly duration formatting and flow/node timing helpers for Pipeline Status.
 * Durations belong on the main ticker (overall flow) and technical graph nodes —
 * not on artificial Level-2 projections of nodes onto ticker steps.
 */

/** @param {string | null | undefined} iso */
export function parseTimeMs(iso) {
	if (iso == null || String(iso).trim() === '') return null;
	const t = Date.parse(String(iso));
	return Number.isNaN(t) ? null : t;
}

/**
 * Compact human duration (no fractional seconds).
 * @param {number} ms
 * @returns {string | undefined}
 */
export function formatFriendlyDuration(ms) {
	if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return undefined;
	const totalSec = Math.floor(ms / 1000);
	if (totalSec < 60) return `${totalSec}s`;
	const totalMin = Math.floor(totalSec / 60);
	if (totalMin < 60) {
		const sec = totalSec % 60;
		return sec > 0 ? `${totalMin}m ${sec}s` : `${totalMin}m`;
	}
	const totalHr = Math.floor(totalMin / 60);
	if (totalHr < 48) {
		const min = totalMin % 60;
		return min > 0 ? `${totalHr}h ${min}m` : `${totalHr}h`;
	}
	const days = Math.floor(totalHr / 24);
	const hr = totalHr % 24;
	return hr > 0 ? `${days}d ${hr}h` : `${days}d`;
}

/**
 * Guard against inverted start/end (clock skew or mixed now-relative vs absolute timestamps).
 * @param {string | null | undefined} startedAt
 * @param {string | null | undefined} endedAt
 * @param {number} [nowMs]
 * @returns {number | undefined}
 */
export function computeDurationMs(startedAt, endedAt, nowMs = Date.now()) {
	const start = parseTimeMs(startedAt);
	if (start == null) return undefined;
	const end = endedAt != null && String(endedAt).trim() !== '' ? parseTimeMs(endedAt) : nowMs;
	if (end == null || end < start) return undefined;
	return end - start;
}

/**
 * @param {{
 *   startedAt?: string | null,
 *   endedAt?: string | null,
 *   state?: 'ready' | 'failed' | 'inProgress' | 'running' | 'complete' | 'success' | 'skipped' | string,
 *   nowMs?: number,
 * }} args
 * @returns {string | undefined}
 */
export function formatFlowDurationLabel(args) {
	const durationMs = computeDurationMs(args.startedAt, args.endedAt, args.nowMs);
	const formatted = formatFriendlyDuration(durationMs ?? Number.NaN);
	if (!formatted) return undefined;
	const state = String(args.state ?? '').toLowerCase();
	if (state === 'ready' || state === 'complete' || state === 'success') {
		return `Completed in ${formatted}`;
	}
	if (state === 'failed') {
		return `Failed after ${formatted}`;
	}
	return `Running for ${formatted}`;
}

/**
 * @param {{
 *   startedAt?: string | null,
 *   completedAt?: string | null,
 *   status?: string,
 *   nowMs?: number,
 * }} args
 * @returns {string | undefined}
 */
export function formatNodeDurationLabel(args) {
	const status = String(args.status ?? '').toLowerCase();
	const ended =
		status === 'success' || status === 'failed' || status === 'complete' || status === 'skipped'
			? args.completedAt
			: undefined;
	const durationMs = computeDurationMs(args.startedAt, ended, args.nowMs);
	const formatted = formatFriendlyDuration(durationMs ?? Number.NaN);
	if (!formatted) return undefined;
	if (status === 'running' || status === 'active') return `Running ${formatted}`;
	if (status === 'failed') return `${formatted} (failed)`;
	return formatted;
}

/**
 * Drop a start that is epoch, or not strictly after every earlier stage on its path.
 * Node duration stays that node's own Started → Completed. Gaps between nodes are left as gaps.
 * @param {import('./pipelineStatusTypes').PipelineStatusStage[]} stages
 * @returns {import('./pipelineStatusTypes').PipelineStatusStage[]}
 */
export function omitPathIllegalStarts(stages) {
	const list = Array.isArray(stages) ? stages : [];
	/** @type {Map<string, import('./pipelineStatusTypes').PipelineStatusStage>} */
	const byId = new Map(list.map((s) => [s.id, s]));
	/** @type {Map<string, number | null>} */
	const floorById = new Map();

	/** @param {import('./pipelineStatusTypes').PipelineStatusStage} stage @param {Set<string>} stack */
	const pathFloorMs = (stage, stack) => {
		if (floorById.has(stage.id)) return floorById.get(stage.id) ?? null;
		let floor = /** @type {number | null} */ (null);
		stack.add(stage.id);
		for (const id of stage.dependsOn || []) {
			if (stack.has(id)) continue;
			const parent = byId.get(id);
			if (!parent) continue;
			const parentMark = positiveTimeMs(parent.completedAt)
				?? positiveTimeMs(parent.startedAt)
				?? positiveTimeMs(parent.occurredAt);
			if (parentMark != null && (floor == null || parentMark > floor)) floor = parentMark;
			const parentFloor = pathFloorMs(parent, stack);
			if (parentFloor != null && (floor == null || parentFloor > floor)) floor = parentFloor;
		}
		stack.delete(stage.id);
		floorById.set(stage.id, floor);
		return floor;
	};

	return list.map((stage) => {
		const start = positiveTimeMs(stage?.startedAt);
		if (!stage || start == null) {
			return stage?.startedAt ? { ...stage, startedAt: undefined } : stage;
		}
		const floor = pathFloorMs(stage, new Set());
		if (floor != null && start <= floor) return { ...stage, startedAt: undefined };
		return stage;
	});
}

/** @param {string | null | undefined} iso */
function positiveTimeMs(iso) {
	const t = parseTimeMs(iso);
	if (t == null || t <= 0) return null;
	return t;
}

/**
 * Overall tracker duration: earliest valid start → latest completion, or now while still running.
 * @param {import('./pipelineStatusTypes').PipelineStatusStage[]} stages
 * @param {string} state
 * @param {number} [nowMs]
 * @param {string | null | undefined} [extraStartedAt]
 * @returns {string | undefined}
 */
export function formatGraphSpanDurationLabel(stages, state, nowMs = Date.now(), extraStartedAt) {
	const list = Array.isArray(stages) ? stages : [];
	let first = /** @type {number | null} */ (null);
	let last = /** @type {number | null} */ (null);
	let open = false;
	for (const stage of list) {
		if (!stage || stage.status === 'pending') continue;
		const start = positiveTimeMs(stage.startedAt);
		if (start != null && (first == null || start < first)) first = start;
		const terminal =
			stage.status === 'success'
			|| stage.status === 'failed'
			|| stage.status === 'complete'
			|| stage.status === 'skipped';
		const end = positiveTimeMs(stage.completedAt);
		if (terminal && end != null && (last == null || end > last)) last = end;
		if (stage.status === 'running') open = true;
	}
	const extra = positiveTimeMs(extraStartedAt);
	if (extra != null && (last == null || extra < last) && (first == null || extra < first)) first = extra;
	if (first == null) return undefined;
	if (last != null && last < first) return undefined;

	const flowState = String(state ?? '').toLowerCase();
	const finished = flowState === 'ready' || flowState === 'failed' || flowState === 'complete' || flowState === 'success';
	const endedAt = finished && !open && last != null ? new Date(last).toISOString() : undefined;
	return formatFlowDurationLabel({
		startedAt: new Date(first).toISOString(),
		endedAt,
		state: flowState === 'failed' ? 'failed' : finished ? 'ready' : 'inProgress',
		nowMs,
	});
}
